import React, {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  useCallback,
} from 'react';
import { IngredientItem } from 'components/customComponents/Layers/InputGroups';
import ELayersView from 'enums/ELayersView';
import 'components/customComponents/Layers/SelectIngredients.scss';
import { GetScreenList, isNullOrUndefined, SetGlobalExpData } from 'utils/Global';
import { ScreenDTO } from 'business/models/Models';
import { withTabBackButton } from 'components/tab';
import { useLayersContainerContext } from 'components/commandpanel/Layers/hooks';
import {
  findTreeNodeById,
  getScreenNamesByEntityId,
  getTreeNodeIdsByType,
} from 'components/commandpanel/Layers/utils';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import LoadingDiv from 'components/generic/loadingDiv';
import useBroadcast, {
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
  MessageType,
} from 'business/broadcast';

interface Props {
  setContentView: (...args) => void;
  setSelectedScreen: (...args) => void;
  handleBackToPreviousScreen: (contentView: ELayersView) => () => void;
  setHandleOnTabBackPress: Dispatch<SetStateAction<(...args) => void>>;
}

function SelectScreen(props: Props) {
  const {
    setContentView,
    setSelectedScreen,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
  } = props;
  const { treeControlState } = useLayersContainerContext();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const treeNodeScreenIds = useMemo(
    () => getTreeNodeIdsByType(treeControlState.treenodes, ETreeNodeTypes.Screen),
    [treeControlState.treenodes],
  );
  const [searchScreen, setSearchScreen] = useState('');
  const [screens, setScreens] = useState<ScreenDTO[]>(() => GetScreenList() ?? []);

  const loadScreens = useCallback(async () => {
    await SetGlobalExpData(true);
    setScreens(GetScreenList() ?? []);
  }, []);

  const screenNamesById = useMemo(
    () => getScreenNamesByEntityId(treeControlState.treenodes),
    [treeControlState.treenodes],
  );

  const screenList = useMemo(() => {
    if (!treeNodeScreenIds?.size) {
      return [];
    }
    return screens
      .filter((screen) => treeNodeScreenIds.has(screen.id))
      .map((screen) => {
        const treeName = screenNamesById.get(screen.id);
        return treeName && treeName !== screen.name
          ? new ScreenDTO({ ...screen, name: treeName })
          : screen;
      })
      .filter((screen) =>
        screen.name?.toLowerCase().includes(searchScreen.toLowerCase()),
      );
  }, [screens, treeNodeScreenIds, searchScreen, screenNamesById]);

  useEffect(() => {
    setHandleOnTabBackPress(() =>
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW),
    );
  }, [setHandleOnTabBackPress]);

  useEffect(() => {
    if (!explorerBroadcastChannel) {
      return;
    }

    const handleBroadcastMessage = (messageEvent: MessageEvent<MessageType<any>>) => {
      const { action, payload } = messageEvent.data;

      if (action === CHANNEL_ACTIONS.EXPLORER_RENAME_NODE) {
        const node = payload as { id: number; name: string };
        if (!node?.id) {
          return;
        }
        const treeNode = findTreeNodeById(treeControlState.treenodes, node.id);
        const screenId =
          treeNode?.type === ETreeNodeTypes.Screen
            ? Number(treeNode.extra?.id)
            : undefined;
        if (!screenId) {
          return;
        }
        setScreens((prev) =>
          prev.map((s) =>
            s.id === screenId ? new ScreenDTO({ ...s, name: node.name }) : s,
          ),
        );
        return;
      }

      if (action === CHANNEL_ACTIONS.EXPLORER_DELETE_NODE) {
        const { deletedNodeIds } = payload as { deletedNodeIds?: number[] };
        if (!deletedNodeIds?.length) {
          return;
        }
        const deletedScreenIds = new Set(
          deletedNodeIds
            .map((id) => findTreeNodeById(treeControlState.treenodes, id))
            .filter((n) => n?.type === ETreeNodeTypes.Screen)
            .map((n) => Number(n.extra?.id)),
        );
        if (deletedScreenIds.size) {
          setScreens((prev) => prev.filter((s) => !deletedScreenIds.has(s.id)));
        }
        return;
      }

      if (
        action === CHANNEL_ACTIONS.EXPLORER_ADD_NODE ||
        action === CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE
      ) {
        loadScreens();
      }
    };

    explorerBroadcastChannel.onmessage = handleBroadcastMessage;
    return () => {
      explorerBroadcastChannel.onmessage = null;
    };
  }, [explorerBroadcastChannel, treeControlState.treenodes, loadScreens]);

  function selectScreen(screen: ScreenDTO) {
    setSelectedScreen(screen);
    setContentView(ELayersView.EDITSCREEN);
    setHandleOnTabBackPress(() => handleBackToPreviousScreen(ELayersView.SELECTSCREEN));
  }

  function changeSearchIngredient(e) {
    setSearchScreen(e.target.value);
  }

  if (treeControlState?.isLoading) {
    return (
      <div className="m-4">
        <LoadingDiv />
      </div>
    );
  }

  return (
    <div id="selectscreen-wrapper" className="select-ingredients-tabs">
      <div
        id="selectscreen-searchbar-wrapper"
        className="flex flex-space-between"
        style={{ padding: '1.45% 4.36%' }}
      >
        <input
          id="selectscreen-searchbar-input"
          type="text"
          placeholder="Search Screen"
          style={{ width: '100%', padding: '1.25% 2.34%', height: '30px' }}
          value={searchScreen}
          onChange={changeSearchIngredient}
        />
      </div>
      <div id="selectscreen-ingredients-container" className="ingredients-container">
        {isNullOrUndefined(screenList)
          ? null
          : screenList.map((screen) => (
              <IngredientItem
                text={screen.name}
                key={screen.id}
                onClick={() => selectScreen(screen)}
              />
            ))}
      </div>
    </div>
  );
}

export default withTabBackButton(SelectScreen);
